import { FC, forwardRef } from "react";

import {
  AddBoxIcon,
  Box,
  BoxProps,
  Button,
  ButtonProps,
  CloseIcon,
  DeleteIcon,
  IconButton,
  PlusIcon,
  Row,
  Tooltip,
} from "@hightouch/htui";

import { Condition, ConditionType } from "src/types/visual";
import {
  FilterPopover,
  FilterPopoverProps,
} from "src/components/explore/filter-popover/filter-popover";
import { colors } from "./colors";
import { useFilterableOptionsContext } from "src/components/explore/filter-popover/filterable-options-hook";
import { FilterOption } from "src/components/explore/filter-popover/constants";

type AndOrButtonProps = {
  conditionType: ConditionType.And | ConditionType.Or;
  onClick(): void;
  isDisabled?: boolean;
  isDisplayOnly?: boolean;
};

export const AndOrToggleButton: FC<Readonly<AndOrButtonProps & BoxProps>> = ({
  conditionType,
  isDisabled = false,
  isDisplayOnly = false,
  onClick,
  ...props
}) => {
  const currentConditionLabel =
    conditionType === ConditionType.And ? "AND" : "OR";
  const toggleToConditionLabel =
    conditionType === ConditionType.And ? "OR" : "AND";

  return (
    <Row
      mr={2}
      width={10}
      sx={{
        button: {
          bg: colors.base[conditionType],
          borderWidth: "hairline",
          borderStyle: "solid",
          borderColor: colors.hover[conditionType],
          color: colors.text[conditionType],
          minWidth: 0,
          p: 2,
          pointerEvents: isDisplayOnly ? "none" : undefined,
          _hover: {
            bg: colors.hover[conditionType],
            borderColor: colors.hover[conditionType],
            color: colors.textHover[conditionType],

            ".button-hidden-icon": {
              color: colors.textHover[conditionType],
              display: "block",
            },
          },
          _disabled: {
            color: "text.tertiary",
            bg: colors.base[conditionType],
            _hover: {
              color: "text.tertiary",
              bg: colors.base[conditionType],
            },
          },
        },
      }}
      {...props}
    >
      <Tooltip message={`Toggle to ${toggleToConditionLabel}`}>
        <Button
          isJustified
          aria-label={
            isDisplayOnly
              ? currentConditionLabel
              : `Toggle to ${toggleToConditionLabel}`
          }
          isDisabled={isDisabled}
          onClick={onClick}
        >
          {currentConditionLabel}
        </Button>
      </Tooltip>
    </Row>
  );
};

interface AddFilterButtonProps<TCondition extends Condition>
  extends FilterPopoverProps<TCondition> {
  conditionType: ConditionType.And | ConditionType.Or;
  /** Render the compact text trigger instead of the filled button. */
  isCompact?: boolean;
  /** Compact labels the trigger with the operator the new filter will join on.
   * False for an empty group, where there is nothing to join. */
  hasPrecedingCondition?: boolean;
}

export function AddFilterButton<TCondition extends Condition>({
  conditionType,
  isCompact = false,
  hasPrecedingCondition = true,
  ...props
}: AddFilterButtonProps<TCondition>): JSX.Element {
  if (isCompact) {
    // Adding the first filter is a primary action and gets a full-size button.
    // Once there's a condition above, the trigger becomes a quiet 24px chip
    // labelled with the operator it will join on — at that point it sits under
    // an existing row and shouldn't compete with it.
    const isPrimaryAction = !hasPrecedingCondition;
    return (
      <FilterPopover
        {...props}
        renderTriggerButton={({ isLoading }) => (
          <Row
            as={Button}
            size={isPrimaryAction ? undefined : "sm"}
            variant="tertiary"
            isLoading={isLoading}
            isDisabled={props.isDisabled}
            icon={PlusIcon}
            alignItems="center"
            // "AND" alone says nothing about what the button does, but the
            // accessible name has to contain the visible label (WCAG 2.5.3) or
            // speech-input users can't activate it by reading it aloud. When the
            // visible label is already "Add filter", the text content is the
            // name and no override is needed.
            aria-label={
              isPrimaryAction
                ? undefined
                : `Add filter joined with ${
                    conditionType === ConditionType.And ? "AND" : "OR"
                  }`
            }
            sx={{
              minWidth: 0,
              px: isPrimaryAction ? 2 : 1,
              ...(isPrimaryAction ? {} : { height: "24px" }),
              color: colors.text[conditionType],
              svg: {
                color: colors.text[conditionType],
                ...(isPrimaryAction ? {} : { height: "16px", width: "16px" }),
              },
              _hover: {
                bg: colors.base[conditionType],
                color: colors.textHover[conditionType],
                svg: { color: colors.textHover[conditionType] },
              },
            }}
          >
            {isPrimaryAction
              ? "Add filter"
              : conditionType === ConditionType.And
                ? "AND"
                : "OR"}
          </Row>
        )}
      />
    );
  }

  return (
    <FilterPopover
      {...props}
      renderTriggerButton={({ isLoading }) => (
        <Row
          as={Button}
          isLoading={isLoading}
          isDisabled={props.isDisabled}
          icon={PlusIcon}
          alignItems="center"
          borderWidth="hairline"
          borderStyle="solid"
          borderColor={colors.hover[conditionType]}
          sx={{
            bg: colors.base[conditionType],
            color: colors.text[conditionType],
            _hover: {
              borderColor: colors.hover[conditionType],
              bg: colors.hover[conditionType],
              color: colors.textHover[conditionType],
              svg: { color: colors.textHover[conditionType] },
            },
            _active: {
              bg: colors.hover[conditionType],
            },
            "&[aria-expanded=true]": {
              boxShadow: "none",
              _focusVisible: {
                boxShadow: "outline",
              },
            },
            svg: {
              color: colors.text[conditionType],
              height: "20px",
              width: "20px",
            },
            _disabled: {
              color: "text.tertiary",
              bg: colors.base[conditionType],
              svg: {
                color: "text.tertiary",
              },
              _hover: {
                color: "text.tertiary",
                svg: {
                  color: "text.tertiary",
                },
                bg: colors.base[conditionType],
              },
            },
          }}
        >
          Add filter
        </Row>
      )}
    />
  );
}

type WhereButtonProps<TCondition extends Condition> =
  FilterPopoverProps<TCondition>;

export function WhereButton<TCondition extends Condition>({
  isDisabled,
  ...props
}: WhereButtonProps<TCondition>): JSX.Element {
  const { sections, isLoading: filterableOptionsAreLoading } =
    useFilterableOptionsContext();
  return (
    <FilterPopover
      {...props}
      renderTriggerButton={({ isLoading }) => (
        <DetailButton
          icon={AddBoxIcon}
          isLoading={isLoading || filterableOptionsAreLoading}
          isDisabled={isDisabled}
          size="sm"
        >
          {sections.length === 1 && sections[0]!.type === FilterOption.Property
            ? "Where property is…"
            : "Where…"}
        </DetailButton>
      )}
    />
  );
}

export const TextButton: FC<Readonly<ButtonProps>> = (props) => (
  <Box
    bg="transparent"
    sx={{
      button: {
        border: "none",
        bg: "transparent",
        _hover: { color: "text.secondary" },
      },
    }}
  >
    {/* biome-ignore lint/suspicious/noTsIgnore: legacy suppression */}
    {/* @ts-ignore icon and imageUrl 'RequireExactlyOne' type not working in spread */}
    <Button color="text.secondary" size="sm" variant="tertiary" {...props} />
  </Box>
);

export const DetailButton: FC<Readonly<ButtonProps>> = forwardRef<
  HTMLDivElement,
  ButtonProps
>(function DetailButtonInternal(props, ref) {
  return (
    <Box
      ref={ref}
      color="text.secondary"
      sx={{
        button: {
          boxShadow: "none",
          _hover: {
            background: "base.backgroundDisabled",
            border: "none",
          },
          _disabled: {
            bg: "inherit",
            color: "text.disabled",
            _hover: {
              bg: "inherit",
              color: "text.disabled",
            },
          },
          bg: "transparent",
          border: "none",
          color: "text.secondary",
          svg: {
            mr: 1,
          },
          // TODO(HTUI-393): stop animating box-shadow (swept in by "common")
          transitionProperty: "common",
          transitionDuration: "fast",
          transitionTimingFunction: "easeOutQuad",
        },
      }}
    >
      {/* biome-ignore lint/suspicious/noTsIgnore: legacy suppression */}
      {/* @ts-ignore icon and imageUrl 'RequireExactlyOne' type not working in spread */}
      <Button {...props}></Button>
    </Box>
  );
});

export const XButton: FC<{ isDisabled?: boolean; onRemove: () => void }> = ({
  isDisabled = false,
  onRemove,
}) => (
  <Tooltip message="Delete this condition" placement="bottom-end">
    <Box
      sx={{
        button: { height: "32px" },
        svg: { height: "20px", width: "20px" },
      }}
    >
      <IconButton
        aria-label="Remove condition."
        isDisabled={isDisabled}
        icon={CloseIcon}
        onClick={onRemove}
      />
    </Box>
  </Tooltip>
);

export const DeleteButton: FC<{
  onRemove: () => void;
  /** Compact rows sit 44px tall, so the control shrinks and drops the danger
   * fill — the row has no space for a filled button. */
  isCompact?: boolean;
}> = ({ onRemove, isCompact = false }) => (
  <Tooltip message="Delete this filter" placement="top-end">
    <IconButton
      aria-label="Delete filter."
      icon={DeleteIcon}
      size={isCompact ? "sm" : undefined}
      variant={isCompact ? "tertiary" : "danger"}
      boxShadow="none"
      onClick={onRemove}
    />
  </Tooltip>
);
